// pages/ConfigurationPage/ConfigurationPage.jsx
// REFACTORED V2 - Configuration-driven approach with generic settings tabs
import { useState, useEffect} from "react";
import { useSelector } from 'react-redux';
import { StandardHeader } from '../../components/common/StandardHeader';
import TabNavigation from '../../components/configuration/sections/TabNavigation';
import GenericSettingsTab from '../../components/configuration/sections/GenericSettingsTab';
import NotificationInfoTab from '../../components/configuration/sections/NotificationInfoTab';
import NotificationRulesTab from '../../components/configuration/sections/NotificationRulesTab';
import EmployeeRatesSettings from '../../components/configuration/sections/EmployeeRatesSettings';
import { getConfigurationTabs, TAB_IDS } from '../../constants/configurationTabs';
import { getSettingsByCategory } from '../../constants/settingsConfig';
import { useConfigurationSettings } from '../../hooks/useConfigurationSettings';


import { 
  getDefaultDateRange, 
  getCurrentCycleMonth,
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';


const ConfigurationPage = () => {
  // Get user role from Redux
  const userRole = useSelector((state) => state.user?.role || 'user');
  
  // Tab navigation state
  const [activeTab, setActiveTab] = useState(TAB_IDS.DEFAULTS);
  
  // Configuration settings state and handlers
  const {
    configs,
    saveStatus,
    updateConfig,
    handleSave,
    handleReset
  } = useConfigurationSettings();


  // ============================================================================
  // EFFECTS
  // ============================================================================
    
    // Set document title and favicon - use shared utility
    useEffect(() => {
      setDocumentMeta(DOCUMENT_TITLES.CONFIGURATION);
    }, []);

  // ============================================================================
  // ============================================================================


  // Get tabs based on user role
  const tabs = getConfigurationTabs(userRole);
  
  // Get the active tab configuration
  const activeTabConfig = tabs.find(tab => tab.id === activeTab);

  // Navigation handler for notification rules
  const handleNavigateToRules = () => {
    setActiveTab(TAB_IDS.NOTIFICATION_RULES);
  };

  // Render the appropriate tab content
  const renderTabContent = () => {
    // Handle custom tabs (non-generic settings)
    if (activeTab === TAB_IDS.EMPLOYEES) {
      return <EmployeeRatesSettings />;
    }

    if (activeTab === TAB_IDS.MY_NOTIFICATIONS) {
      return (
        <NotificationInfoTab
          userRole={userRole}
          onNavigateToRules={handleNavigateToRules}
        />
      );
    }

    if (activeTab === TAB_IDS.NOTIFICATION_RULES && userRole === 'admin') {
      return <NotificationRulesTab />;
    }

    // Handle generic settings tabs
    if (activeTabConfig?.type === 'settings' && activeTabConfig.categoryKey) {
      const categoryConfig = getSettingsByCategory(activeTabConfig.categoryKey);
      
      return (
        <GenericSettingsTab
          categoryConfig={categoryConfig}
          configs={configs}
          updateConfig={updateConfig}
          saveStatus={saveStatus}
          onSave={handleSave}
          onReset={handleReset}
        />
      );
    }

    // Fallback
    return (
      <div className="text-center py-8 text-gray-500">
        Tab content not found
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="layout-container responsive-space">
        {/* Header */}
        <StandardHeader
          title="Configuration Settings"
          subtitle="Manage system-wide settings and preferences"
        />

        {/* Main Content Card */}
        <div className="responsive-space">
          {/* Tab Navigation */}
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Tab Content */}
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPage;